import type { ReactNode } from 'react';
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from '@tanstack/react-router';
import appCss from '~/styles/app.css?url';
import { NotFound } from '~/components/NotFound';
import { DefaultCatchBoundary } from '~/components/DefaultCatchBoundary';
import { AppLayout } from '~/layouts/AppLayout';
import { MantineProvider } from '@mantine/core';
import { theme } from '~/styles/theme';
import { QueryClient } from '@tanstack/react-query';
import { getHeader } from '@tanstack/react-start/server';
import { createServerFn } from '@tanstack/react-start';

const getHostDetails = createServerFn({
	method: 'GET',
	response: 'data',
}).handler((ctx) => {
	const host = getHeader('host') || getHeader('referer');
	const isLionsTech = host?.includes('lionstech.io') || false;
	return {
		host,
		logo: isLionsTech ? '/ChampIO.png' : '/champtek_horizontal.svg',
		isLionsTech,
	};
});

export const Route = createRootRouteWithContext<{
	queryClient: QueryClient;
}>()({
	loader: () => {
		return getHostDetails();
	},
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
			{
				title: 'champtek :: Delivering Beyond Expectation',
			},
			{
				name: 'description',
				content:
					'champtek is a provider of software development and testing services. Having talented software engineers on board, we craft compelling web, desktop, and mobile applications for our clients.',
			},
		],
		links: [
			{
				rel: 'icon',
				href: '/champtek_logo.svg',
				type: 'image/svg',
			},
			{
				rel: 'stylesheet',
				href: appCss,
			},
		],
	}),
	errorComponent: (props) => {
		return (
			<RootDocument>
				<DefaultCatchBoundary {...props} />
			</RootDocument>
		);
	},
	notFoundComponent: () => <NotFound />,
	component: RootComponent,
});

function RootComponent() {
	return (
		<RootDocument>
			<Outlet />
		</RootDocument>
	);
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
	const data = Route.useLoaderData();
	return (
		<html>
			<head>
				<HeadContent />
			</head>
			<body>
				<MantineProvider theme={theme(data?.isLionsTech || false)}>
					<AppLayout
						isLionsTech={data?.isLionsTech || false}
						logo={data?.logo || '/champtek_horizontal.svg'}
						children={children}
					/>
				</MantineProvider>
				<Scripts />
			</body>
		</html>
	);
}
